import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import dayjs from 'dayjs';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { CommunityModelFactory } from '../CommunityModelFactory';
import { ICollaboratorPermission } from '../CommunityClient';
import { IPendingCareMapInvite } from '../../caremap/ICareMapInvite';
import { CareMapState } from '../../caremap/CareMapState';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { AlertPresenter } from '../../AlertBinder';
import { AppPrincipal } from '../../auth/AppPrincipal';
import { UserState } from '../../user/UserState';
import { Spinner } from '../../components/Spinner';
import { Text } from '../../components/Text';
import { IAction, Menu } from '../../components/Menu';
import { DeleteIcon } from '../../theme/Icons';
import { PersonAvatar } from '../../avatar/Avatars';
import { IAvatarIdentity } from '../../avatar/IAvatarIdentity';
import { AvatarIdentityFactory, ICareRecipientAvatar } from '../../avatar/AvatarIdentityFactory';
import { accessLabels, accessRemovedEvents, invitationCancelledTypes } from '../accessLevels';

// Unified people list for the Sharing & Access sheet: owner + co-owners + viewers +
// pending invites in one list. Removal reuses the exact tracking the production
// CollaboratorRowAdapter fires (Invitation Cancelled / Co-Owner Removed / Permanent
// Viewer Removed), and a row that links to a care-map member shows its relationship
// tag and an "Update in Care Map" action.

const useStyles = createUseStyles({
    list: {
        display: 'flex',
        flexDirection: 'column'
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 0',
        borderBottom: '1px solid var(--ion-color-step-100)'
    },
    main: {
        flex: 1,
        minWidth: 0
    },
    status: {
        fontSize: 11,
        color: 'var(--ion-color-medium)',
        marginTop: 4,
        display: 'flex',
        gap: 6,
        alignItems: 'center',
        flexWrap: 'wrap'
    },
    rolePill: {
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 7px',
        borderRadius: 999
    },
    pillCo: {
        background: 'rgba(146,61,172,0.12)',
        color: 'var(--ion-color-tertiary)'
    },
    pillView: {
        background: 'rgba(17,120,157,0.10)',
        color: 'var(--ion-color-primary)'
    },
    pillOwner: {
        background: 'var(--ion-color-step-100)',
        color: 'var(--ion-color-step-750)'
    },
    pillContributor: {
        background: 'rgba(var(--ion-color-success-rgb), 0.12)',
        color: 'var(--ion-color-success-shade)'
    },
    relationshipTag: {
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 7px',
        borderRadius: 999,
        background: 'var(--ion-color-step-50)',
        color: 'var(--ion-color-step-750)',
        border: '1px solid var(--ion-color-step-100)'
    },
    statusSep: {
        color: 'var(--ion-color-step-400)'
    },
    menuSlot: {
        marginLeft: 4
    },
    loadingWrap: {
        minHeight: 120,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    }
});

type IRoleDisplay = 'Owner' | 'Co-Owner' | 'Viewer' | 'Contributor';

const displayName = (row: { name: string }): string => row.name || '(no name)';

interface IRow {
    key: string;
    // empty when the person has no name; displayName gives the label
    name: string;
    email: string;
    photo?: string;
    // The authorised route for `photo`, when it came from a care map member. Absent when the
    // photo is a collaborator's own profile picture, which has no audited route yet.
    photoHref?: string;
    photoHasNoAuditedRoute?: IAvatarIdentity['photoHasNoAuditedRoute'];
    avatar?: string;
    color?: string;
    // set when the person is the care recipient, who is drawn by the care recipient's own rules
    careRecipientAvatar?: ICareRecipientAvatar;
    role: IRoleDisplay;
    isOwner: boolean;
    statusLine: string;
    careMapRole?: string;
    linkedMemberId?: string;
    onRemove?: () => Promise<void>;
}

const formatDate = (iso?: string): string => {
    if (!iso) {
        return '';
    }
    return dayjs(iso).format('M/D/YY');
};

export interface ISharingPeopleListProps {
    /** Called from a linked row's "Update in Care Map" action with the linked member's id. */
    onUpdateInCareMap: (memberId: string) => void;
}

export const SharingPeopleList: FC<ISharingPeopleListProps> = ({ onUpdateInCareMap }) => {
    const classes = useStyles();

    const factory = useInstance(CommunityModelFactory);
    useSubscription(() => factory);
    const community = factory.forActiveCaremap();

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const tracking = useInstance(MixPanelEventEmitter);
    const alert = useInstance(AlertPresenter);
    const principal = useInstance(AppPrincipal);
    const userState = useInstance(UserState);
    const avatarIdentities = useInstance(AvatarIdentityFactory);

    if (!community.isLoaded()) {
        return (
            <div className={classes.loadingWrap}>
                <Spinner />
            </div>
        );
    }

    const patientFirstName = careMapState.getActiveCaremap()?.patient?.firstName || 'this care recipient';

    const confirmRemove = async (name: string): Promise<boolean> => {
        const choice = await alert.showAlertV2({
            header: `Remove ${name}'s access?`,
            message: `${name} will no longer be able to see ${patientFirstName}'s care. This doesn't remove them from the care map.`,
            maskInReplay: true,
            options: [
                { label: 'Cancel', id: 'cancel' },
                { label: 'Remove Access', id: 'remove', role: 'destructive' }
            ]
        });
        return choice === 'remove';
    };

    const rows: IRow[] = [];

    const owner = community.getOwner();
    if (owner) {
        // Resolve photo/avatar/color for the owner. community.getOwner() returns only
        // { userId, email, name } — no avatar fields — so we look them up from the caremap.
        const activeCaremap = careMapState.getActiveCaremap();
        const patient = activeCaremap?.patient;
        // Priority 1: patient node linked to the owner account (common for for-self maps).
        // Priority 2: any household member linked to the owner account.
        const ownerMember =
            (patient?.linkedUserId === owner.userId ? patient : undefined) ||
            activeCaremap?.household.members.find(m => m.linkedUserId === owner.userId);
        // Priority 3 (current-user fallback): if the viewer IS the owner, use UserState's
        // profile picture — but only then, to avoid showing the viewer's photo for someone else.
        const isOwnerCurrentUser = owner.userId === principal.userId;
        const ownerPhoto = ownerMember?.photo ?? (isOwnerCurrentUser ? userState.state.pictureUrl : undefined);
        const ownerAvatar = ownerMember?.avatar ?? (isOwnerCurrentUser ? userState.state.avatar : undefined);
        const ownerColor = ownerMember?.color ?? (isOwnerCurrentUser ? userState.state.color : undefined);
        rows.push({
            key: 'owner',
            name: owner.name || '',
            email: owner.email || '',
            photo: ownerPhoto,
            photoHref: ownerMember?.photo ? ownerMember.links?.photo?.href : undefined,
            photoHasNoAuditedRoute: ownerMember?.photo ? undefined : true,
            avatar: ownerAvatar,
            color: ownerColor,
            careRecipientAvatar: activeCaremap && patient?.linkedUserId === owner.userId ? avatarIdentities.forCareRecipient(activeCaremap) : undefined,
            role: 'Owner',
            isOwner: true,
            statusLine: 'Innerhive owner'
        });
    }

    const buildPermissionRow = (permission: ICollaboratorPermission, role: IRoleDisplay): IRow => {
        const link = careMapState.findMemberLinkForInvite({
            email: permission.invite.acceptedBy.email,
            accepterUserId: permission.invite.acceptedBy.userId
        });
        const collaborator = permission.collaborator;
        // Name resolution priority: (1) linked local member's current name,
        // (2) server collaborator projection, (3) frozen invite name.
        const name = link
            ? careMapState.getMemberDisplayName(link.member)
            : (collaborator?.name || permission.invite.intendedRecipient.name || '');
        // Photo/avatar/color only available on household members (IFamilyMember), not support-group members.
        const householdMember = link?.hiveKey === 'household' ? link.member : undefined;
        return {
            key: `perm-${permission.invite.token}`,
            name,
            email: permission.invite.acceptedBy.email || '',
            photo: householdMember?.photo || collaborator?.pictureUrl,
            photoHref: householdMember?.photo ? householdMember.links?.photo?.href : undefined,
            photoHasNoAuditedRoute: householdMember?.photo ? undefined : true,
            avatar: householdMember?.avatar || collaborator?.avatar,
            color: householdMember?.color || collaborator?.color,
            role,
            isOwner: false,
            statusLine: `Joined ${formatDate(permission.consentedAt)}`,
            // a collaborator outside the care map (a contributor) still has the relationship the inviter gave
            careMapRole: link?.label || permission.relationship,
            linkedMemberId: link?.member.id,
            onRemove: principal.userId !== permission.userId ? async () => {
                tracking.track(accessRemovedEvents[permission.access]);
                await community.deleteCollaborator(permission.invite.token);
            } : undefined
        };
    };

    const buildInviteRow = (inv: IPendingCareMapInvite): IRow => {
        const link = careMapState.findMemberLinkForInvite({ email: inv.intendedRecipient.email });
        // Name resolution priority: (1) linked local member's current name,
        // (2) frozen invite name (no collaborator projection for pending invites — no account yet).
        const name = link
            ? careMapState.getMemberDisplayName(link.member)
            : (inv.intendedRecipient.name || '');
        const householdMember = link?.hiveKey === 'household' ? link.member : undefined;
        return {
            key: `inv-${inv.token}`,
            name,
            email: inv.intendedRecipient.email || '',
            photo: householdMember?.photo,
            photoHref: householdMember?.links?.photo?.href,
            avatar: householdMember?.avatar,
            color: householdMember?.color,
            role: accessLabels[inv.access],
            isOwner: false,
            statusLine: `Invited ${formatDate(inv.createdAt)}`,
            careMapRole: link?.label,
            linkedMemberId: link?.member.id,
            onRemove: inv.links.self ? async () => {
                tracking.track('Invitation Cancelled', () => ({ type: invitationCancelledTypes[inv.access] }));
                await community.deleteInvite(inv.token);
            } : undefined
        };
    };

    // Group by status (accepted before invited), then role (co-owner, contributor, viewer),
    // ordering people alphabetically by display name within each group.
    const byName = (a: IRow, b: IRow): number => displayName(a).localeCompare(displayName(b), undefined, { sensitivity: 'base' });
    rows.push(
        ...community.getPermanentCoOwners().map(c => buildPermissionRow(c, 'Co-Owner')).sort(byName),
        ...community.getPermanentContributors().map(c => buildPermissionRow(c, 'Contributor')).sort(byName),
        ...community.getPermanentViewers().map(v => buildPermissionRow(v, 'Viewer')).sort(byName),
        ...community.getInvites('co-owner').map(buildInviteRow).sort(byName),
        ...community.getInvites('contributor').map(buildInviteRow).sort(byName),
        ...community.getInvites('view-only').map(buildInviteRow).sort(byName)
    );

    const pillClassFor = (role: IRoleDisplay): string => {
        if (role === 'Co-Owner') {
            return `${classes.rolePill} ${classes.pillCo}`;
        }
        if (role === 'Viewer') {
            return `${classes.rolePill} ${classes.pillView}`;
        }
        if (role === 'Contributor') {
            return `${classes.rolePill} ${classes.pillContributor}`;
        }
        return `${classes.rolePill} ${classes.pillOwner}`;
    };

    const buildActions = (row: IRow): IAction[] => {
        return [
            {
                text: 'Update in Care Map',
                hidden: !row.linkedMemberId,
                'data-type': 'update-care-map',
                handler: () => {
                    if (row.linkedMemberId) {
                        onUpdateInCareMap(row.linkedMemberId);
                    }
                }
            },
            {
                text: 'Remove Access',
                role: 'destructive',
                Icon: DeleteIcon,
                'data-type': 'remove-access',
                // Use hidden (conditional render) rather than disabled: a disabled IonItem inside
                // the menu's keepContentsMounted popover can keep a stale `disabled` attribute
                // (pointer-events:none) even once removal becomes allowed, blocking the click.
                hidden: !row.onRemove,
                handler: async () => {
                    if (!row.onRemove) {
                        return;
                    }
                    if (await confirmRemove(displayName(row))) {
                        await row.onRemove();
                    }
                }
            }
        ];
    };

    return (
        <div className={classes.list} data-id="sharing-people-list">
            {rows.map(row => {
                const actions = buildActions(row);
                // Only render the menu when at least one action is visible — otherwise the
                // three-dot button opens an empty popover (e.g. the current user's own
                // unlinked co-owner/viewer row, which can't be removed and has no member to update).
                const showMenu = !row.isOwner && actions.some(action => !action.hidden);
                return (
                    <div key={row.key} className={classes.row} data-type="sharing-row">
                        <div className="mp-mask">
                            {row.careRecipientAvatar ? (
                                <PersonAvatar small {...row.careRecipientAvatar} />
                            ) : (
                                <PersonAvatar
                                    small
                                    identity={{
                                        photo: row.photo,
                                        avatar: row.avatar,
                                        color: row.color,
                                        links: row.photoHref ? { photo: { href: row.photoHref } } : undefined,
                                        photoHasNoAuditedRoute: row.photoHasNoAuditedRoute
                                    }}
                                    name={row.name}
                                />
                            )}
                        </div>
                        <div className={classes.main}>
                            <Text type="subtitle" data-type="recipient-name"><span className="mp-mask">{displayName(row)}</span></Text>
                            {row.email && <Text type="subdetail" data-type="recipient-email"><span className="mp-mask">{row.email}</span></Text>}
                            <div className={classes.status}>
                                <span className={pillClassFor(row.role)} data-type="recipient-role">{row.role}</span>
                                {row.careMapRole && (
                                    <span className={classes.relationshipTag}>{row.careMapRole}</span>
                                )}
                                <span className={classes.statusSep}>·</span>
                                <span data-type="recipient-status">{row.statusLine}</span>
                            </div>
                        </div>
                        {showMenu && (
                            <div className={classes.menuSlot}>
                                <Menu actions={actions} data-id={`row-menu-${row.key}`} />
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};
